'use client';

import type { MouseEvent } from 'react';
import { type ReactNode } from 'react';
import {
  Badge,
  BodyDefault,
  BodyStrong,
  MarkdownPreview,
  MetaDefault,
} from '../..';
import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { useDateFormatter } from '@terra/ui-formatters';
import { useTranslations } from 'next-intl';
import { LinkSquare02Icon } from '@terra/ui-icons';
import type { TracePreviewBaseDto } from '@terra/shared-contracts-trace';
import {
  getConfidenceColor,
  getRankBadgeStyle,
} from '../../utils/signal-badges.utils';
import {
  formatCategoryForDisplay,
  formatEnumForDisplay,
} from '../../utils/category-utils';
import { SignalRunsTable } from './SignalRunsTable';
import { SignalStatusBadge } from './SignalStatusBadge';
import type { SignalDto } from '@terra/shared-contracts-signals';
import { toDisplaySignalOrigin } from '@terra/shared-contracts-signals';
import type {
  SignalAssetTypeType,
  SignalOriginType,
} from '@terra/shared-schemas-orchestrator';
import { SignalOriginSchema } from '@terra/shared-schemas-orchestrator';
import { isArchivedLifecycle } from '@terra/shared-dto-types';
import { LinkedFindingRow } from '../findings/LinkedFindingRow';
import { FindingChainFlow } from '../findings/finding-chain-graph';
import { ArchivedLifecycleBadge } from '../../components/archived-lifecycle-badge';

type FindingRowDto = {
  id: string;
  title: string | null;
  status: string | null;
  createdAt: string | Date;
  severity?: string | null;
  discoveryDate?: string | Date | null;
  isArchived?: boolean;
};

export type SignalMarkableSectionParams = {
  field: string;
  fieldLabel: string;
  fullText: string;
  children: ReactNode;
};
type SignalContentProps = {
  signal: SignalDto;
  traces?: TracePreviewBaseDto[];
  linkedFindings?: FindingRowDto[];
  chainedFindings?: FindingRowDto[];
  onViewTrace?: (trace: TracePreviewBaseDto) => void;
  onNavigateToEndpoint?: (endpointId: string, endpointUrl: string) => void;
  onNavigateToSurfaceAsset?: (
    assetId: string,
    assetType: SignalAssetTypeType,
  ) => void;
  onNavigateToFinding?: (findingId: string) => void;
  onFindingsContextMenu?: (
    event: MouseEvent<HTMLElement>,
    findingId: string,
  ) => void;
  onEndpointContextMenu?: (
    event: MouseEvent<HTMLElement>,
    endpointId: string,
    endpointUrl: string,
  ) => void;
  onSurfaceAssetContextMenu?: (
    event: MouseEvent<HTMLElement>,
    assetId: string,
    assetType: SignalAssetTypeType,
  ) => void;
  onTraceContextMenu?: (
    event: MouseEvent<HTMLElement>,
    trace: TracePreviewBaseDto,
  ) => void;
  shouldDisplayRuns?: boolean;
  showMetadata?: boolean;
  formatOriginLabel?: (origin: SignalOriginType) => string;
  renderMarkableSection?: (params: SignalMarkableSectionParams) => ReactNode;
  surfaceAssetLabel?: string;
};

function SignalSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <VStack gap={2} alignItems="stretch" w="full">
      <BodyStrong>{title}</BodyStrong>
      {children}
    </VStack>
  );
}

function SignalDetailRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <HStack gap={3} alignItems="center" minH={6} w="full">
      <BodyDefault color="fg.muted" w="110px" flexShrink={0} truncate>
        {label}
      </BodyDefault>
      <Box minW={0} flex="1">
        {children}
      </Box>
    </HStack>
  );
}

function SignalMarkdownSection({
  title,
  field,
  fieldLabel,
  fullText,
  renderMarkableSection,
  children,
}: {
  title: string;
  field: string;
  fieldLabel: string;
  fullText: string;
  renderMarkableSection?: SignalContentProps['renderMarkableSection'];
  children: ReactNode;
}) {
  const content = renderMarkableSection
    ? renderMarkableSection({ field, fieldLabel, fullText, children })
    : children;

  return (
    <SignalSection title={title}>
      <Box w="full" color="fg.muted">
        {content}
      </Box>
    </SignalSection>
  );
}

export const SignalContent = ({
  signal,
  traces,
  linkedFindings = [],
  chainedFindings = [],
  onViewTrace,
  onNavigateToEndpoint,
  onNavigateToSurfaceAsset,
  onNavigateToFinding,
  onFindingsContextMenu,
  onEndpointContextMenu,
  onSurfaceAssetContextMenu,
  onTraceContextMenu,
  shouldDisplayRuns = true,
  showMetadata = true,
  formatOriginLabel,
  renderMarkableSection,
  surfaceAssetLabel,
}: SignalContentProps) => {
  const t = useTranslations('designSystem.signals');
  const tRuns = useTranslations('designSystem.signals.runs');
  const dateFormatter = useDateFormatter();

  if (!signal) {
    return null;
  }

  const isAppWide = signal.isAppWide === true;
  const url = signal.endpoint?.url || '';
  const endpointId = signal.endpoint?.id;
  const surfaceAssetId = signal.assetId;
  const surfaceAssetType = signal.assetType;
  const hasNavigableSurfaceAsset =
    Boolean(surfaceAssetId) &&
    Boolean(surfaceAssetType) &&
    Boolean(onNavigateToSurfaceAsset);
  const surfaceAssetDisplayLabel =
    surfaceAssetLabel ||
    (surfaceAssetId && surfaceAssetType
      ? `${surfaceAssetType} (${
          surfaceAssetId.length > 8
            ? `${surfaceAssetId.slice(0, 4)}…${surfaceAssetId.slice(-4)}`
            : surfaceAssetId
        })`
      : '');
  const runsTraces = traces && traces.length > 0 ? traces : signal.traces;
  const visibleRunsTraces = runsTraces?.filter(
    (trace) => trace.name !== 'neo-post-attack',
  );
  const runsCount = visibleRunsTraces?.length ?? 0;
  const canNavigateToEndpoint = Boolean(endpointId && onNavigateToEndpoint);
  const displayedRelatedAssetId = url ? endpointId : surfaceAssetId;
  const isRelatedAssetArchived = isArchivedLifecycle(
    signal.relatedAssets?.find((asset) => asset.id === displayedRelatedAssetId)
      ?.lifecycle,
  );
  const shouldShowRuns = shouldDisplayRuns && runsCount > 0;
  const hasRelatedSections =
    chainedFindings.length > 0 || shouldShowRuns || linkedFindings.length > 0;
  const hasNarrativeSections = Boolean(
    signal.analysis ||
      signal.reason ||
      signal.securityTestCaseSummary ||
      signal.smokeTestSummary,
  );
  const originLabel =
    formatOriginLabel?.(signal.origin) ??
    formatEnumForDisplay(toDisplaySignalOrigin(signal.origin));

  return (
    <VStack gap={4} alignItems="stretch" w="full">
      {signal.description ? (
        <SignalMarkdownSection
          title={t('drawer.description')}
          field="description"
          fieldLabel={t('drawer.description')}
          fullText={signal.description}
          renderMarkableSection={renderMarkableSection}
        >
          <MarkdownPreview source={signal.description ?? ''} fontSize={14} />
        </SignalMarkdownSection>
      ) : null}
      <SignalSection title={t('drawer.origin')}>
        <Badge variant="fill" size="sm" w="fit-content">
          <MetaDefault>{originLabel}</MetaDefault>
        </Badge>
      </SignalSection>
      {showMetadata ? (
        <HStack gap={4} flexWrap="wrap" w="full">
          <HStack gap={2} alignItems="center">
            <BodyStrong color="fg.muted">{t('drawer.category')}:</BodyStrong>
            <Badge variant="fill">
              <MetaDefault>
                {formatCategoryForDisplay(signal.category)}
              </MetaDefault>
            </Badge>
          </HStack>

          {signal.confidence !== null && (
            <HStack gap={2} alignItems="center">
              <BodyStrong color="fg.muted">
                {t('drawer.confidence')}:
              </BodyStrong>
              <BodyStrong color={getConfidenceColor(signal.confidence)}>
                {t('drawer.confidenceValue', {
                  value: signal.confidence,
                })}
              </BodyStrong>
            </HStack>
          )}

          <HStack gap={2} alignItems="center">
            <BodyStrong color="fg.muted">{t('drawer.rank')}:</BodyStrong>
            <Badge variant="fill" style={getRankBadgeStyle(signal.rank)}>
              <MetaDefault>{signal.rank}</MetaDefault>
            </Badge>
          </HStack>

          <HStack gap={2} alignItems="center">
            <BodyStrong color="fg.muted">{t('drawer.label')}:</BodyStrong>
            <SignalStatusBadge
              status={signal.status}
              stage={signal.stage}
              confirmedVulnerabilityFindingState={
                signal.confirmedVulnerabilityFindingState
              }
              blockedReason={signal.blockedReason}
            />
          </HStack>
        </HStack>
      ) : null}
      {isAppWide || url || hasNavigableSurfaceAsset ? (
        <SignalSection title={t('drawer.asset')}>
          {isAppWide && !url ? (
            <VStack gap={1} alignItems="stretch">
              <BodyStrong>{t('drawer.appWideNoEndpointsTitle')}</BodyStrong>
              <BodyDefault color="fg.muted">
                {t('drawer.appWideNoEndpointsDescription')}
              </BodyDefault>
            </VStack>
          ) : url ? (
            <HStack
              gap={2}
              alignItems="center"
              fontSize="sm"
              color="fg.muted"
              cursor={canNavigateToEndpoint ? 'pointer' : 'default'}
              onContextMenu={(event) => {
                if (endpointId && onEndpointContextMenu) {
                  onEndpointContextMenu(event, endpointId, url);
                }
              }}
              onClick={
                canNavigateToEndpoint && endpointId
                  ? () => onNavigateToEndpoint?.(endpointId, url)
                  : undefined
              }
              _hover={
                canNavigateToEndpoint ? { color: 'fg.default' } : undefined
              }
            >
              <LinkSquare02Icon size={14} />
              <BodyDefault
                lineClamp={1}
                title={canNavigateToEndpoint ? `Go to endpoint: ${url}` : url}
                flex="1"
                _hover={
                  canNavigateToEndpoint
                    ? { textDecoration: 'underline' }
                    : undefined
                }
              >
                {url}
              </BodyDefault>
              {isRelatedAssetArchived ? (
                <ArchivedLifecycleBadge lifecycle={{ state: 'ARCHIVED' }} />
              ) : null}
            </HStack>
          ) : (
            hasNavigableSurfaceAsset &&
            surfaceAssetId &&
            surfaceAssetType && (
              <HStack
                gap={2}
                alignItems="center"
                fontSize="sm"
                color="fg.muted"
                cursor="pointer"
                onContextMenu={(event) => {
                  onSurfaceAssetContextMenu?.(
                    event,
                    surfaceAssetId,
                    surfaceAssetType,
                  );
                }}
                onClick={() =>
                  onNavigateToSurfaceAsset?.(surfaceAssetId, surfaceAssetType)
                }
                _hover={{ color: 'fg.default' }}
              >
                <LinkSquare02Icon size={14} />
                <BodyDefault
                  lineClamp={1}
                  title={`Go to asset: ${surfaceAssetDisplayLabel}`}
                  flex="1"
                  _hover={{ textDecoration: 'underline' }}
                >
                  {surfaceAssetDisplayLabel}
                </BodyDefault>
                {isRelatedAssetArchived ? (
                  <ArchivedLifecycleBadge lifecycle={{ state: 'ARCHIVED' }} />
                ) : null}
              </HStack>
            )
          )}
        </SignalSection>
      ) : null}
      {hasRelatedSections ? (
        <VStack
          gap={4}
          alignItems="stretch"
          w="full"
          pt={4}
          borderTopWidth="1px"
          borderColor="border.subtle"
        >
          {chainedFindings.length > 0 ? (
            <SignalSection
              title={t('drawer.chainedFindingsCount', {
                count: chainedFindings.length,
              })}
            >
              <FindingChainFlow
                findings={chainedFindings.map((finding) => ({
                  id: finding.id,
                  title: finding.title,
                  status: finding.status,
                  severity: finding.severity ?? null,
                  discoveryDate:
                    finding.discoveryDate ?? finding.createdAt ?? null,
                  isArchived: finding.isArchived,
                }))}
                orderedIds={
                  signal.origin === SignalOriginSchema.enum.FINDING_CHAINING
                    ? signal.constituentFindingIds
                    : chainedFindings.map((finding) => finding.id)
                }
                onFindingClick={onNavigateToFinding}
                onFindingContextMenu={onFindingsContextMenu}
              />
            </SignalSection>
          ) : null}
          {shouldShowRuns ? (
            <SignalSection title={`${tRuns('title')} (${runsCount})`}>
              <SignalRunsTable
                traces={visibleRunsTraces}
                onViewTrace={onViewTrace}
                onTraceContextMenu={onTraceContextMenu}
                markArchived={isArchivedLifecycle(signal.lifecycle)}
              />
            </SignalSection>
          ) : null}
          {linkedFindings.length > 0 ? (
            <SignalSection title={t('drawer.relatedFindings')}>
              <VStack gap={2} alignItems="stretch">
                {linkedFindings.map((finding) => (
                  <LinkedFindingRow
                    key={finding.id}
                    finding={finding}
                    onNavigate={onNavigateToFinding}
                    onContextMenu={onFindingsContextMenu}
                  />
                ))}
              </VStack>
            </SignalSection>
          ) : null}
        </VStack>
      ) : null}
      {hasNarrativeSections ? (
        <VStack
          gap={4}
          alignItems="stretch"
          w="full"
          pt={4}
          borderTopWidth="1px"
          borderColor="border.subtle"
        >
          {signal.analysis ? (
            <SignalMarkdownSection
              title={t('drawer.analysis')}
              field="analysis"
              fieldLabel={t('drawer.analysis')}
              fullText={signal.analysis}
              renderMarkableSection={renderMarkableSection}
            >
              <MarkdownPreview source={signal.analysis} fontSize={14} />
            </SignalMarkdownSection>
          ) : null}
          {signal.reason ? (
            <SignalMarkdownSection
              title={t('drawer.reason')}
              field="reason"
              fieldLabel={t('drawer.reason')}
              fullText={signal.reason}
              renderMarkableSection={renderMarkableSection}
            >
              <MarkdownPreview source={signal.reason} fontSize={14} />
            </SignalMarkdownSection>
          ) : null}
          {signal.securityTestCaseSummary ? (
            <SignalMarkdownSection
              title={t('drawer.securityTestCase')}
              field="securityTestCaseSummary"
              fieldLabel={t('drawer.securityTestCase')}
              fullText={signal.securityTestCaseSummary}
              renderMarkableSection={renderMarkableSection}
            >
              <MarkdownPreview
                source={signal.securityTestCaseSummary}
                fontSize={14}
                showCodeLineNumbers
              />
            </SignalMarkdownSection>
          ) : null}
          {signal.smokeTestSummary ? (
            <SignalMarkdownSection
              title={t('drawer.smokeTestSummary')}
              field="smokeTestSummary"
              fieldLabel={t('drawer.smokeTestSummary')}
              fullText={signal.smokeTestSummary}
              renderMarkableSection={renderMarkableSection}
            >
              <MarkdownPreview source={signal.smokeTestSummary} fontSize={14} />
            </SignalMarkdownSection>
          ) : null}
        </VStack>
      ) : null}
      <SignalSection title={t('drawer.timestamps')}>
        <VStack gap={2} alignItems="stretch">
          <SignalDetailRow label={t('drawer.createdAt')}>
            <BodyDefault>
              {signal.createdAt
                ? dateFormatter.fromString(String(signal.createdAt))
                : '-'}
            </BodyDefault>
          </SignalDetailRow>
          <SignalDetailRow label={t('drawer.updatedAt')}>
            <BodyDefault>
              {signal.updatedAt
                ? dateFormatter.fromString(String(signal.updatedAt))
                : '-'}
            </BodyDefault>
          </SignalDetailRow>
        </VStack>
      </SignalSection>
    </VStack>
  );
};
